/**
 * A web page as an OPEN ITEM.
 *
 * The desktop app is not a browser, and the moment you notice is when a url
 * takes you somewhere the project isn't: a second window, outside the layout,
 * outside Home, gone on refresh. So a page is modelled as the same kind of thing
 * a document is — it opens on the canvas, it appears in Home, it is addressable.
 *
 * The id CARRIES the url (`web:https://example.com/x`) rather than pointing at a
 * record of it. Everything that resolves an open item takes an id and nothing
 * else: the `?file=` param after a refresh, and useOpenItems, which stores ids
 * only so titles stay live. A page has no catalog entry to resolve against, so
 * it has to be self-describing or it could not survive either path.
 *
 * http(s) ONLY, validated on the way in and again on the way out. An id is
 * user-typed and round-trips through a url and localStorage, so `javascript:` or
 * `file:` reaching a webview's src is exactly the shape this must refuse.
 */

import type { Subject } from '../../shell/window/subject'

const PREFIX = 'web:'

/**
 * Can THIS host hold a web page as an open item?
 *
 * Only the desktop app can. It has Electron's `<webview>`: a real browser view
 * in its own process, which no site can refuse. The web client has only an
 * iframe, and the sites you actually reach for (google, dribbble, anything with
 * a login) send `X-Frame-Options: SAMEORIGIN` and render blank — with no event
 * to detect it, so the app cannot even say what went wrong.
 *
 * A browser tab does not need a browser inside it. So on the web client a url
 * is not an open item at all: it opens a TAB, which is the right answer there
 * rather than a degraded one, and nothing that cannot render is ever put on the
 * canvas or into Home.
 *
 * A capability probe rather than a user-agent sniff: the tag exists only when
 * the shell's window enabled `webviewTag`, and a UA check would claim it in an
 * Electron build that has not.
 */
export function canEmbedWebPages(): boolean {
  if (typeof document === 'undefined') return false
  try {
    const el = document.createElement('webview') as { reload?: unknown }
    return typeof el.reload === 'function'
  } catch {
    return false
  }
}

export const isWebId = (id: string): boolean => id.startsWith(PREFIX)

/** The url behind a web id, or null if it is not one (or not http(s)). */
export function webUrlOf(id: string): string | null {
  if (!isWebId(id)) return null
  try {
    const u = new URL(id.slice(PREFIX.length))
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.href : null
  } catch {
    return null
  }
}

/**
 * The label for a page, before it has told us its own title: the host, minus
 * the `www.` nobody reads. Short enough for a 180px sidebar row, and stable —
 * derived from the id, so Home and the breadcrumbs cannot disagree.
 */
export function webTitleOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

/** A page as an open item. Null for anything that is not an http(s) url. */
export function webFileFor(url: string): Subject | null {
  let href: string
  try {
    const u = new URL(url)
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null
    href = u.href
  } catch {
    return null
  }
  return {
    id: `${PREFIX}${href}`,
    title: webTitleOf(href),
    // The full url: it is what the header shows and what a hover reveals.
    path: href,
    mime: 'text/html',
  }
}

/** The open item for a web id, or null. The id-only half of webFileFor. */
export function webFileFromId(id: string): Subject | null {
  const url = webUrlOf(id)
  return url ? webFileFor(url) : null
}
